import { ArrowLeft, Check, Circle, Mail, Route } from "lucide-react";
import { useState, type FC } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { LoadingState } from "@/components/brand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";

import { ClientPortalAPI } from "../api";
import { useClientPortal } from "../components/ClientPortalShell";
import { CaseSummary, PortalCard } from "../components/PortalParts";
import { usePortalT } from "../i18n";
import {
  explainerText,
  formatDay,
  relativeDay,
  updateLine
} from "../portalModel";
import { useClientPortalCase, useClientPortalHome } from "../queries";
import { CLIENT_PORTAL_CASE_PATH, clientPortalCasePath } from "../routes";
import type {
  PortalCaseDetail,
  PortalReceipt,
  PortalReceiptStatus,
  PortalTask
} from "../types";
import { PortalLoadError } from "./PortalHomePage";

/** `/portal/case`: one case opens directly; several are listed. */
export const PortalCasesPage: FC = () => {
  const { t } = usePortalT();
  const home = useClientPortalHome();
  if (home.isLoading) return <LoadingState size="lg" />;
  if (!home.data)
    return <PortalLoadError onRetry={() => void home.refetch()} />;
  const { cases } = home.data;
  if (cases.length === 1)
    return <Navigate to={clientPortalCasePath(cases[0].id)} replace />;
  if (!cases.length) {
    return (
      <Empty className="border border-border bg-background">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Route />
          </EmptyMedia>
          <EmptyTitle>{t("case.none")}</EmptyTitle>
          <EmptyDescription>{t("case.noneBody")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight">
        {t("case.titlePlural")}
      </h1>
      {cases.map((c) => (
        <PortalCard key={c.id}>
          <CaseSummary portalCase={c} link />
        </PortalCard>
      ))}
    </>
  );
};

const Timeline: FC<{ detail: PortalCaseDetail; tasks: PortalTask[] }> = ({
  detail,
  tasks
}) => {
  const { t } = usePortalT();
  return (
    <ol className="mt-2 flex flex-col">
      {detail.stages.map((stage, i) => (
        <li
          key={stage.key}
          className="grid grid-cols-[26px_minmax(0,1fr)] gap-3.5"
          aria-current={stage.state === "now" ? "step" : undefined}
        >
          <span className="flex flex-col items-center">
            <span
              className={cn(
                "mt-px flex size-5.5 shrink-0 items-center justify-center rounded-full",
                stage.state === "done" && "bg-success text-white",
                stage.state === "now" && "bg-primary text-primary-foreground",
                stage.state === "next" && "border border-line-3"
              )}
            >
              {stage.state === "done" ? (
                <Check className="size-3" strokeWidth={3} />
              ) : null}
              {stage.state === "now" ? (
                <span className="size-1.75 rounded-full bg-primary-foreground" />
              ) : null}
            </span>
            {i < detail.stages.length - 1 ? (
              <span
                className={cn(
                  "my-1 min-h-5.5 w-0.5 flex-1",
                  stage.state === "done" ? "bg-success" : "bg-border"
                )}
              />
            ) : null}
          </span>
          <span className="flex flex-col gap-1 pb-5">
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "text-md font-semibold",
                  stage.state === "next" && "text-muted-foreground"
                )}
              >
                {t(`stage.${stage.key}`)}
              </span>
              {stage.state === "now" ? (
                <Badge variant="secondary">{t("case.nowChip")}</Badge>
              ) : null}
            </span>
            <span
              className={cn(
                "max-w-135 text-sm",
                stage.state === "next" && "text-muted-foreground"
              )}
            >
              {stage.state === "now"
                ? explainerText(detail, t)
                : t(`stageAbout.${stage.key}`)}
            </span>
            {stage.state === "now" && tasks.length ? (
              <div className="mt-1.5 flex flex-col gap-2 rounded-lg border border-border px-3 py-2.5">
                <span className="text-sm font-semibold">
                  {t("case.waitingOnYou")}
                </span>
                {tasks.map((task) => (
                  <span
                    key={task.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Circle className="size-3.5 shrink-0 text-primary" />
                    <span className="flex-1">{task.title}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
};

const ReceiptRow: FC<{ caseId: string; receipt: PortalReceipt }> = ({
  caseId,
  receipt
}) => {
  const { t, i18n } = usePortalT();
  const [status, setStatus] = useState<PortalReceiptStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const check = () => {
    setChecking(true);
    setFailed(false);
    ClientPortalAPI.receiptStatus(caseId, receipt.id)
      .then(setStatus)
      .catch(() => setFailed(true))
      .finally(() => setChecking(false));
  };
  return (
    <li className="flex flex-col gap-1.5 border-t border-line-2 pt-3 first:border-t-0 first:pt-0">
      <span className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium">{receipt.number}</span>
        <span className="flex-1" />
        <Button size="sm" variant="outline" disabled={checking} onClick={check}>
          {checking ? t("case.checking") : t("case.checkStatus")}
        </Button>
      </span>
      {status?.status ? (
        <span className="flex flex-col gap-0.5 text-sm">
          <span className="font-medium">{status.status}</span>
          {status.description ? (
            <span className="text-muted-foreground">{status.description}</span>
          ) : null}
          {status.checked_at ? (
            <span className="text-xs text-muted-foreground">
              {t("case.checkedAt", {
                when: relativeDay(status.checked_at, t, i18n.language)
              })}
            </span>
          ) : null}
        </span>
      ) : null}
      {failed ? (
        <span className="text-sm text-destructive">
          {t("case.statusFailed")}
        </span>
      ) : null}
    </li>
  );
};

const Details: FC<{ detail: PortalCaseDetail }> = ({ detail }) => {
  const { t, i18n } = usePortalT();
  const me = useClientPortal();
  const lead = me.team.find((m) => m.name === detail.lead) ?? me.team[0];
  const rows: [string, string | null, string?][] = [
    [t("case.caseType"), detail.title],
    [t("case.attorney"), detail.lead],
    [
      t("case.priorityDate"),
      detail.priority_date
        ? formatDay(detail.priority_date, i18n.language)
        : null,
      t("case.priorityLater")
    ],
    [
      t("case.opened"),
      detail.opened_at ? formatDay(detail.opened_at, i18n.language) : null
    ]
  ];
  return (
    <>
      <PortalCard title={t("case.details")}>
        <dl className="flex flex-col gap-1.5 text-sm">
          {rows
            .filter(([, value, empty]) => value || empty)
            .map(([label, value, empty]) => (
              <div key={label} className="flex gap-2.5">
                <dt className="w-27.5 shrink-0 text-muted-foreground">
                  {label}
                </dt>
                <dd className={cn(!value && "text-fg-3")}>{value ?? empty}</dd>
              </div>
            ))}
        </dl>
      </PortalCard>
      <PortalCard title={t("case.receipts")}>
        {detail.receipts.length ? (
          <ul className="flex flex-col gap-3">
            {detail.receipts.map((r) => (
              <ReceiptRow key={r.id} caseId={detail.id} receipt={r} />
            ))}
          </ul>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("case.noReceipts")}
          </span>
        )}
      </PortalCard>
      <PortalCard title={t("case.updates")}>
        {detail.updates.length ? (
          <ul className="flex flex-col gap-2">
            {detail.updates.map((u) => (
              <li
                key={`${u.kind}-${u.at}`}
                className="flex items-start gap-3 text-sm"
              >
                <span className="w-13 shrink-0 text-muted-foreground">
                  {formatDay(u.at, i18n.language)}
                </span>
                <span>{updateLine(u, t)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("case.noUpdates")}
          </span>
        )}
      </PortalCard>
      {lead?.email ? (
        <PortalCard>
          <span className="flex items-center gap-2.5">
            <Mail className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm">{t("case.questions")}</span>
            <Button asChild variant="outline" size="sm">
              <a href={`mailto:${lead.email}`}>
                {t("case.emailLead", { name: lead.name.split(" ")[0] })}
              </a>
            </Button>
          </span>
        </PortalCard>
      ) : null}
    </>
  );
};

export const PortalCasePage: FC = () => {
  const { caseId } = useParams();
  const { t, i18n } = usePortalT();
  const detail = useClientPortalCase(caseId);
  const home = useClientPortalHome();

  if (detail.isLoading) return <LoadingState size="lg" />;
  if (!detail.data) {
    return (
      <Empty className="border border-border bg-background">
        <EmptyHeader>
          <EmptyTitle>{t("case.notFound")}</EmptyTitle>
        </EmptyHeader>
        <Button asChild variant="outline">
          <Link to={CLIENT_PORTAL_CASE_PATH}>{t("case.back")}</Link>
        </Button>
      </Empty>
    );
  }
  const d = detail.data;
  const multiple = (home.data?.cases.length ?? 0) > 1;
  const subtitle = [
    t("case.step", { n: d.stage_index + 1, total: d.stages.length }),
    d.opened_at
      ? t("case.startedOn", { date: formatDay(d.opened_at, i18n.language) })
      : null
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <>
      {multiple ? (
        <Button
          asChild
          variant="link"
          size="sm"
          className="h-auto self-start p-0"
        >
          <Link to={CLIENT_PORTAL_CASE_PATH}>
            <ArrowLeft className="size-3.5 rtl:rotate-180" />
            {t("case.back")}
          </Link>
        </Button>
      ) : null}
      <div className="grid items-start gap-5 lg:grid-cols-[1.6fr_1fr]">
        <PortalCard className="md:p-6">
          <header className="flex flex-col gap-0.5">
            <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
              {d.title}
            </h1>
            <span className="text-sm text-muted-foreground">{subtitle}</span>
          </header>
          {d.on_hold ? (
            <CaseSummary portalCase={d} />
          ) : (
            <Timeline detail={d} tasks={home.data?.tasks ?? []} />
          )}
        </PortalCard>
        <div className="flex flex-col gap-5">
          <Details detail={d} />
        </div>
      </div>
    </>
  );
};
